import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CpdLabeledValue from '@/components/ui/CpdLabeledValue';
import { formatTimestamp } from '@/utils/date/date';
import SftpConnectionImportRowsTable from './SftpConnectionImportRowsTable';

const SftpConnectionImportDetail = ({
  organizationId,
  connectionId,
  selectedImport,
  detailLoading,
  downloadLoading,
  onDownloadSourceFile,
}) => {
  if (!selectedImport) return null;

  return (
    <Paper sx={{ mt: 3, p: 3 }} variant='outlined'>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}>
        <Box>
          <Typography variant='h6'>Import Detail</Typography>
          <Typography variant='body2' color='text.secondary'>
            {selectedImport.source_file_name}
          </Typography>
        </Box>
        <Button
          variant='outlined'
          startIcon={
            downloadLoading ? <CircularProgress size={16} /> : <DownloadIcon />
          }
          disabled={!selectedImport.file_id || downloadLoading}
          onClick={onDownloadSourceFile}>
          Download Source File
        </Button>
      </Box>

      {detailLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {selectedImport.fatal_error_code && (
            <Alert severity='error' sx={{ mb: 2 }}>
              <strong>{selectedImport.fatal_error_code}</strong>
              {selectedImport.fatal_error_message
                ? `: ${selectedImport.fatal_error_message}`
                : ''}
            </Alert>
          )}

          {selectedImport.seen_count > 1 && (
            <Alert severity='info' sx={{ mb: 2 }}>
              Duplicate uploads reused this import report and were not
              reprocessed.
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <CpdLabeledValue label='Import ID' value={selectedImport.id} />
            </Grid>
            <Grid item xs={12} md={4}>
              <CpdLabeledValue
                label='File ID'
                value={selectedImport.file_id || '-'}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CpdLabeledValue
                label='Workflow ID'
                value={selectedImport.workflow_id || '-'}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CpdLabeledValue
                label='Source Path'
                value={selectedImport.source_remote_path}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CpdLabeledValue
                label='Source Size'
                value={selectedImport.source_size_bytes}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CpdLabeledValue
                label='Source Modified'
                value={formatTimestamp(selectedImport.source_modified_at)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CpdLabeledValue
                label='Notification Email'
                value={
                  selectedImport.connection_notification_email ||
                  'Not configured'
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CpdLabeledValue
                label='Content Hash'
                value={selectedImport.content_hash || '-'}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CpdLabeledValue
                label='Source Fingerprint'
                value={selectedImport.source_fingerprint || '-'}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CpdLabeledValue
                label='Seen Count'
                value={selectedImport.seen_count ?? 1}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CpdLabeledValue
                label='Last Seen At'
                value={formatTimestamp(selectedImport.last_seen_at)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CpdLabeledValue
                label='Last Seen File ID'
                value={selectedImport.last_seen_file_id || '-'}
              />
            </Grid>
          </Grid>

          <Typography variant='h6' sx={{ mb: 2 }}>
            Row Outcomes
          </Typography>
          <SftpConnectionImportRowsTable
            organizationId={organizationId}
            connectionId={connectionId}
            importId={selectedImport.id}
          />
        </>
      )}
    </Paper>
  );
};

export default SftpConnectionImportDetail;
